import { useCallback, useMemo, useState } from 'react';
import { useUserInfo } from '@/hooks';
import {
  BUSINESS_ROLE_GROUPS,
  userHasPermission,
} from '@/components/security/AppRoles';
import { DriverApi } from '../models';
import { getDriversTableColumns } from '../config';
import { useDriverStatusActions } from './index';

type PendingToggle = {
    driverId: string;
    nextValue: boolean;
    driverName: string;
};

const useDriversTable = (drivers: DriverApi[]) => {
    const { permissions } = useUserInfo();
    const canManageActive = userHasPermission(
      permissions,
      BUSINESS_ROLE_GROUPS.management
    );

    const { onConfirmDriverStatus, isEnabling } = useDriverStatusActions();

    const driverNameById = useMemo(() => {
        return drivers.reduce<Record<string, string>>((acc, driver) => {
            const fullName = `${driver.firstName || ''} ${driver.lastName || ''}`.trim();
            acc[driver.id] = fullName;
            return acc;
        }, {});
    }, [drivers]);

    const [pendingToggle, setPendingToggle] = useState<PendingToggle | null>(null);

    const handleToggleRequest = useCallback(
        (driverId: string, nextValue: boolean) => {
            setPendingToggle({
                driverId,
                nextValue,
                driverName: driverNameById[driverId] || '',
            });
        },
        [driverNameById]
    );

    const handleCloseModal = useCallback(() => {
        setPendingToggle(null);
    }, []);

    const handleConfirm = useCallback(async () => {
        if (!pendingToggle || isEnabling) return;
        const { driverId, nextValue } = pendingToggle;
        const success = await onConfirmDriverStatus(driverId, nextValue);
        if (success) {
            setPendingToggle(null);
        }
    }, [isEnabling, onConfirmDriverStatus, pendingToggle]);

    const columns = useMemo(() => {
        return getDriversTableColumns({
            onToggleRequest: handleToggleRequest,
            canManageActive,
        });
    }, [handleToggleRequest, canManageActive]);

    const modalState = useMemo(
        () => ({
            open: Boolean(pendingToggle),
            isActivating: pendingToggle?.nextValue ?? true,
            driverName: pendingToggle?.driverName,
        }),
        [pendingToggle]
    );

    return {
        columns,
        modalState,
        isEnabling,
        handleCloseModal,
        handleConfirm,
    };
};

export default useDriversTable;
